"use client"

import {
  useQualitySuggestionsQuery,
  useSelfLearningSummaryQuery,
  useUnresolvedTopicsQuery,
} from "@/hooks/queries/conversation-quality"
import { SKIP_REASONS } from "@/lib/agents/messaging/skip-reasons"
import type { QualityPageTab, QualityTabLanding } from "@/lib/conversation-quality/page-tabs"

import {
  HandedToTeamTile,
  OutcomesCard,
  ReviewQueue,
  SelfServiceTile,
} from "../sections/outcome-sections"
import { CustomersWaitingTile } from "../sections/missed-reply-sections"
import { SelfLearningCard } from "../sections/self-learning-card"
import { UnresolvedSummary } from "../sections/topic-sections"
import {
  ErrorAlert,
  NoFinishedSessionsNotice,
  useFinishedSessions,
  type QualityTabProps,
} from "./tab-parts"

/** How the agent did: three tiles, what it learned, how sessions ended, and a glance at what it could learn. */
export function OverviewTab({
  onOpenTab,
  ...props
}: QualityTabProps & { onOpenTab: (tab: QualityPageTab, landing?: QualityTabLanding) => void }) {
  const { quality, input, ready, viewerTimezone } = props
  const { outcomes, outcomesError, isEmpty } = useFinishedSessions(props)
  const on = quality === "on"
  // The same keys Learning & improvements reads, so opening it reuses them.
  const { data: topics, error: topicsError } = useUnresolvedTopicsQuery(input, ready && on)
  const { data: suggestions } = useQualitySuggestionsQuery({ agentId: input.agentId }, on)
  const { data: learning, error: learningError } = useSelfLearningSummaryQuery({ agentId: input.agentId }, on)
  const showOutcomes = quality !== "off" && !outcomesError
  // The waiting count reads markers, not closed sessions, so it stays under an empty period's notice; it waits for
  // the profile timezone its key carries.
  const tiles = [
    ...(showOutcomes && !isEmpty
      ? [<SelfServiceTile key="resolved" data={outcomes} />, <HandedToTeamTile key="handed" data={outcomes} />]
      : []),
    ...(ready
      ? [
          <CustomersWaitingTile
            key="waiting"
            input={input}
            onOpenAll={() => onOpenTab("volume", { missed: SKIP_REASONS.AI_PAUSED_OR_DISABLED })}
          />,
        ]
      : []),
  ]

  return (
    <div className="space-y-3">
      {isEmpty && <NoFinishedSessionsNotice />}
      {tiles.length > 0 && <div className="grid gap-3 md:grid-cols-3">{tiles}</div>}
      {/* All-time, so it stays under an empty period's notice too. */}
      {quality !== "off" &&
        (learningError ? (
          <ErrorAlert error={learningError} />
        ) : (
          <SelfLearningCard
            data={learning}
            suggestions={suggestions}
            topics={topics}
            timezone={viewerTimezone}
            onReview={() => onOpenTab("learning")}
            onAllChanges={() => onOpenTab("learning", { section: "changes" })}
            onQuestions={() => onOpenTab("learning", { section: "questions" })}
          />
        ))}
      {!isEmpty &&
        quality !== "off" &&
        (outcomesError ? (
          <ErrorAlert error={outcomesError} />
        ) : (
          <OutcomesCard data={outcomes} timezone={input.timezone} />
        ))}
      {ready &&
        !isEmpty &&
        quality !== "off" &&
        (topicsError ? (
          <ErrorAlert error={topicsError} />
        ) : (
          <UnresolvedSummary data={topics} waiting={suggestions?.waiting} onOpen={() => onOpenTab("learning")} />
        ))}
      {/* The queue does not follow the period, so it stays under an empty period's notice. */}
      {showOutcomes && <ReviewQueue data={outcomes} />}
    </div>
  )
}
